import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Services from './components/Services';
import Counter from './components/Counter';
import VideoSection from './components/VideoSection';
import Pricing from './components/Pricing';
import Treatments from './components/Treatments';
import Partners from './components/Partners';
import Blog from './components/Blog';
import CallToAction from './components/CallToAction';
import Footer from './components/Footer';
import About from './pages/About';
import Program from './pages/Program';
import FAQ from './pages/FAQ';
import Experts from './pages/Experts';
import BlogPage from './pages/BlogPage';
import BlogPost from './pages/BlogPost';
import Contact from './pages/Contact';
import WhatsAppButton from './components/WhatsAppButton';
import { useFacebookPixel } from './hooks/useFacebookPixel';

const PageTransition = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  useFacebookPixel();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

const BackgroundEffects = () => (
  <>
    {/* Gradient de fond principal */}
    <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(201,171,129,0.03)_0%,rgba(0,0,0,0)_70%)] opacity-60" />
    
    {/* Overlay texturé subtil */}
    <div className="fixed inset-0 bg-[url('data:image/png;base64,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')] opacity-[0.02]" />
    
    {/* Vignettes latérales */}
    <div className="fixed top-0 left-0 w-1/4 h-screen bg-gradient-to-r from-[rgba(201,171,129,0.05)] to-transparent pointer-events-none" />
    <div className="fixed top-0 right-0 w-1/4 h-screen bg-gradient-to-l from-[rgba(201,171,129,0.05)] to-transparent pointer-events-none" />
    
    {/* Particules animées */}
    <div className="fixed inset-0 pointer-events-none">
      <div className="absolute w-2 h-2 bg-[#c9ab81] rounded-full animate-float1 opacity-20" style={{top: '10%', left: '20%'}} />
      <div className="absolute w-1 h-1 bg-[#c9ab81] rounded-full animate-float2 opacity-15" style={{top: '30%', right: '15%'}} />
      <div className="absolute w-1.5 h-1.5 bg-[#c9ab81] rounded-full animate-float3 opacity-25" style={{bottom: '25%', left: '40%'}} />
    </div>
  </>
);

const Home = () => {
  return (
    <div className="min-h-screen overflow-hidden">
      <BackgroundEffects />
      
      <Hero />
      <Services />
      <Counter />
      <VideoSection />
      <Pricing />
      <Treatments />
      <Partners />
      <Blog />
      <CallToAction />
    </div>
  );
};

function App() {
  return (
    <Router>
      <div className="min-h-screen overflow-hidden">
        <Navbar />
        
        <PageTransition>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/program" element={<Program />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/experts" element={<Experts />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:id" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </PageTransition>

        <Footer />
        <WhatsAppButton phoneNumber="+23058241702" email="auraimmersionilemaurice@gmail.com" location="Maurice" />
      </div>
    </Router>
  );
}

export default App;